import React, { useState } from "react";

import Box from "veneer/Box";
import Divider from "veneer/Divider";
import Stack from "veneer/Stack";
import styled from "veneer/styled";
import TextButton from "veneer/TextButton";
import { Colors } from "veneer/Theme";
import Typography from "veneer/Typography";

import type {
  DeviceNetworkTypeFilter,
  LastConnectedTier,
} from "cortex/ui/src/fleet/view/device/model";
import type { LastSeenDeviceCounts } from "cortex/ui/src/redux-store/last-seen-devices/selectors";
import {
  Indicator,
  type IndicatorColor,
  type IndicatorType,
} from "cortex/ui/src/ui/indicator";
import SegmentedControl, {
  SegmentedControlButton,
} from "cortex/ui/src/ui/segmented-control";
import { Panel } from "cortex/ui/src/view/layout";

export interface LastSeenStatusPanelProps {
  counts: LastSeenDeviceCounts;
  onTierClick?: (
    tier: LastConnectedTier,
    networkType?: DeviceNetworkTypeFilter,
  ) => void;
}

interface StatusRowProps {
  color: IndicatorColor;
  count: number;
  icon: IndicatorType;
  label: string;
  onCountClick?: () => void;
}

interface DisplayCounts {
  connected: number;
  hibernated: number;
  missedCheckin: number;
  offline: number;
  unreachable: number;
}

type LastSeenView = "production" | "sandbox";

const LabelTypography = styled(Typography)(({ theme }) => ({
  ...theme.typography.h5,
  color: Colors.gray700,
  fontFamily: "Aeonik",
  textAlign: "center",
}));

function formatCount(count: number): string {
  return count.toLocaleString();
}

function getProductionCount(total: number, sandboxTesting: number): number {
  return Math.max(0, total - sandboxTesting);
}

function getDisplayCounts(
  counts: LastSeenDeviceCounts,
  view: LastSeenView,
): DisplayCounts {
  if (view === "sandbox") {
    return {
      connected: counts.connectedSandboxTestingDevices,
      hibernated: counts.hibernatedSandboxTestingDevices,
      missedCheckin: counts.missedCheckinSandboxTestingDevices,
      offline: counts.offlineSandboxTestingDevices,
      unreachable: counts.unreachableSandboxTestingDevices,
    };
  }

  return {
    connected: getProductionCount(
      counts.connectedDevices,
      counts.connectedSandboxTestingDevices,
    ),
    hibernated: getProductionCount(
      counts.hibernatedDevices,
      counts.hibernatedSandboxTestingDevices,
    ),
    missedCheckin: getProductionCount(
      counts.missedCheckinDevices,
      counts.missedCheckinSandboxTestingDevices,
    ),
    offline: getProductionCount(
      counts.offlineDevices,
      counts.offlineSandboxTestingDevices,
    ),
    unreachable: getProductionCount(
      counts.unreachableDevices,
      counts.unreachableSandboxTestingDevices,
    ),
  };
}

function StatusRow({
  color,
  count,
  icon,
  label,
  onCountClick,
}: StatusRowProps) {
  const testId = `last-seen-count-${label.toLowerCase().replace(/\s+/g, "-")}`;

  return (
    <Stack alignItems="center" direction="row" gap={1} p={1}>
      <Indicator color={color} size="medium" type={icon} />
      <Stack flex={1}>
        <Typography color={Colors.gray1000} variant="h6">
          {label}
        </Typography>
      </Stack>
      {onCountClick ? (
        <TextButton
          aria-label={`Filter devices by ${label}`}
          dataTestId={testId}
          onClick={onCountClick}
        >
          {formatCount(count)}
        </TextButton>
      ) : (
        <Typography color={Colors.gray1000}>{formatCount(count)}</Typography>
      )}
    </Stack>
  );
}

export default function LastSeenStatusPanel({
  counts,
  onTierClick,
}: LastSeenStatusPanelProps) {
  const [view, setView] = useState<LastSeenView>("production");
  const displayCounts = getDisplayCounts(counts, view);

  const handleViewChange = (value?: string) => {
    if (value === "production" || value === "sandbox") {
      setView(value);
    }
  };

  const handleTierClick = (tier: LastConnectedTier) => {
    const networkType = view === "sandbox" ? "Sandbox" : "Production";
    onTierClick?.(tier, networkType);
  };

  return (
    <Panel mb={0}>
      <Stack alignItems="stretch" direction="row" gap={2}>
        <Box
          alignItems="center"
          display="flex"
          flex={1}
          flexDirection="column"
          justifyContent="center"
          maxWidth={264}
          textAlign="center"
        >
          <LabelTypography>Connected</LabelTypography>
          <Box alignItems="center" display="flex" gap={1.5}>
            <Indicator color="green" size="large" type="triangle-up" />
            <TextButton
              aria-label="Filter devices by Connected"
              dataTestId="last-seen-count-connected"
              onClick={() => handleTierClick("Connected")}
            >
              <Typography color="inherit" variant="display1">
                {formatCount(displayCounts.connected)}
              </Typography>
            </TextButton>
          </Box>
          {displayCounts.hibernated > 0 && (
            <TextButton
              aria-label="Filter devices by Hibernating"
              dataTestId="last-seen-count-hibernating"
              onClick={() => handleTierClick("Hibernating")}
            >
              <Typography color="inherit" variant="body2">
                {formatCount(displayCounts.hibernated)} Hibernating
              </Typography>
            </TextButton>
          )}
          <Box mt={2} width="100%">
            <SegmentedControl
              dataTestId="last-seen-status-view"
              fullWidth
              onChange={handleViewChange}
              size="small"
            >
              <SegmentedControlButton
                selected={view === "production"}
                value="production"
              >
                Production
              </SegmentedControlButton>
              <SegmentedControlButton
                selected={view === "sandbox"}
                value="sandbox"
              >
                Sandbox
              </SegmentedControlButton>
            </SegmentedControl>
          </Box>
        </Box>
        <Divider flexItem orientation="vertical" />
        <Stack divider={<Divider />} flex={1.5} minWidth={180} spacing={0.5}>
          <StatusRow
            color="yellow"
            count={displayCounts.missedCheckin}
            icon="triangle-down"
            label="Missed Check-in"
            onCountClick={() => handleTierClick("Missed Check-in")}
          />
          <StatusRow
            color="darkYellow"
            count={displayCounts.offline}
            icon="triangle-down"
            label="Offline"
            onCountClick={() => handleTierClick("Offline")}
          />
          <StatusRow
            color="red"
            count={displayCounts.unreachable}
            icon="triangle-down"
            label="Unreachable"
            onCountClick={() => handleTierClick("Unreachable")}
          />
        </Stack>
      </Stack>
    </Panel>
  );
}
